
import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Input, Button, Tooltip } from 'antd'
import { FiInfo } from 'react-icons/fi'
import { api } from '../../../../../../../../../services'
import { useSelector } from 'react-redux'
import dummyUserImg from '../../../../../../../../../assets/user.svg'
import { SendOutlined } from '@ant-design/icons'

const AiChat = ({
    generateImage,
    isImageBeingGenerated,
    autoStart = false
}) => {
    const { t: tChat } = useTranslation()
    const defaultMessages = [
        {
            user: false,
            message: tChat('v5:what_type_image_generate')
        }
    ]

    const [isChatStarted, setIsChatStarted] = useState(false)
    const [userInput, setUserInput] = useState('')
    const [messages, setMessages] = useState(defaultMessages)
    const [isReceivingMessage, setIsReceivingMessage] = useState(false)
    const [context, setContext] = useState([])
    const [revisedPrompt, setRevisedPrompt] = useState('')

    const messagesEndRef = useRef(null)

    const handleSubmit = () => {
        if (!userInput) return
        const data = {
            params: {
                prompt: userInput,
                context: context
            }
        }
        setMessages([...messages, { user: true, message: userInput }])
        setUserInput('')
        setIsReceivingMessage(true)
        api.imageGeneration.conversationAI(data).then(response => {
            const body = response.data.body
            setMessages(prevMsgs => [
                ...prevMsgs,
                { user: false, message: body && body[0].response }
            ])
            setContext(body && body[0].context)
            setRevisedPrompt(body && body[0].revised_prompt)
            setIsReceivingMessage(false)
        })
    }

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            })
        }
    }

    useEffect(() => {
        if (userInput && !isReceivingMessage) return
        scrollToBottom()
    }, [messages, userInput, isReceivingMessage])

    const handleStartNewChat = () => {
        setIsChatStarted(true)
        setMessages(defaultMessages)
        setContext([])
        setRevisedPrompt('')
    }

    const user = useSelector(state => state.user)

    const userImg =
        (user && user.info && user.info.avatar && user.info.avatar.link) ||
        dummyUserImg
    const { t } = useTranslation()
    const renderChat = () => {
        return (
            <div className="ai-chat-card">
                <div className="ai-chat-header">
                    <div className="ai-chat-title">
                        {t('v4:prompt_assistant')}
                        <Tooltip title={t('v4:talk_to_ai_about_the_image_generate')}>
                            <span className="ai-chat-info" aria-label="info">
                                <FiInfo />
                            </span>
                        </Tooltip>
                    </div>
                </div>
                <div className="ai-chat-messages">
                    {messages.map((message, index) => (
                        <div
                            className={`ai-chat-row ${message.user ? 'ai-chat-row--user' : 'ai-chat-row--assistant'
                                }`}
                            key={index}
                        >
                            <div className="ai-chat-avatar">
                                {message.user ? (
                                    <img src={userImg} alt={t('v5:user_alt')} />
                                ) : (
                                    <span>{t('v4:ai')}</span>
                                )}
                            </div>
                            <div className="ai-chat-bubble">{message.message}</div>
                        </div>
                    ))}

                    {revisedPrompt && !isReceivingMessage && (
                        <div className="ai-chat-row ai-chat-row--assistant">
                            <div className="ai-chat-avatar">
                                <span>{t('v4:ai')}</span>
                            </div>
                            <div className="ai-chat-bubble">
                                <Button
                                    onClick={handleChatButton}
                                    disabled={isImageBeingGenerated}
                                >
                                    {t('v4:generate_image_using_suggested_prompt')}
                                </Button>
                            </div>
                        </div>
                    )}

                    {isReceivingMessage && (
                        <div className={`ai-chat-row ai-chat-row--assistant`}>
                            <div className="ai-chat-avatar">
                                <span>{t('v4:ai')}</span>
                            </div>
                            <div className="ai-chat-bubble">
                                <span className="ai-chat-typing">
                                    <span className="ai-chat-dot"></span>
                                    <span className="ai-chat-dot"></span>
                                    <span className="ai-chat-dot"></span>
                                </span>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                <div
                    className="ai-chat-input"
                >
                    <Input.Group compact style={{ display: 'flex' }}>
                        <Input
                            placeholder={t('v4:ask_for_prompt_suggestions')}
                            className="ai-chat-input-field"
                            value={userInput}
                            onChange={e => setUserInput(e.target.value)}
                            onPressEnter={e => {
                                e.preventDefault()
                                e.stopPropagation()
                                handleSubmit()
                            }}
                            disabled={isReceivingMessage || isImageBeingGenerated}
                            style={{ flex: 1 }}
                        />
                        <Button
                            type="primary"
                            onClick={handleSubmit}

                            disabled={isReceivingMessage || isImageBeingGenerated || !userInput}
                        >
                            <SendOutlined />
                        </Button>
                    </Input.Group>
                </div>
            </div>
        )
    }

    const handleChatButton = () => {
        generateImage(revisedPrompt)

        setIsChatStarted(!isChatStarted)
    }

    useEffect(() => {
        if (autoStart && !isChatStarted) {
            handleStartNewChat()
        }
        if (!autoStart && isChatStarted) {
            // Stop and clear when toggled off
            setIsChatStarted(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoStart])

    return (
        <div className="ai-chat-container">{isChatStarted && renderChat()}</div>
    )
}

export default AiChat
